import { Users, UserCheck, Send, Pause, Play } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';

import { useGlobalStates } from "../globalConstants";
import ChatHeader from './chatHeader';

export default function Chat() {
    const {messages, setMessages, socket, sessionStatus, setSessionStatus, completed, setCompleted, setWrapUp, isPlaying, setIsPlaying, playingAudio, setPlayingAudio} = useGlobalStates();
    const [selectedAgents, setSelectedAgents] = useState('both');
    const [inputMessage, setInputMessage] = useState('');

    const [audioQueue, setAudioQueue] = useState([]);
    const [isAgentsResponding, setIsAgentsResponding] = useState(false);
    const [wrapUpCount, setWrapUpCount] = useState(0);

    const containerRef = useRef(null);

    useEffect(() => {
        if (containerRef.current) {
            containerRef.current.scrollTop = containerRef.current.scrollHeight;
        }
    }, [messages]);

    const currentAudioRef = useRef(null);

    const API_BASE_URL = process.env.REACT_APP_SOCKET_SERVER;

    useEffect(() => {
        socket.on("agent_message", (data) => {
            setMessages((prev) => [...prev, data]);

            if (data.audio) {
                const audioUrl = `${API_BASE_URL}${data.audio}`;
                setAudioQueue((prev) => [...prev, { url: audioUrl, speaker: data.speaker }]);
            }

            setIsAgentsResponding(false);  // Agent responded, hide loading
        });

        socket.on("updated_session_status", (data) => {
            setSessionStatus(data);
        });

        socket.on("wrap_up_count", (data) => {
            console.log(data);
            setWrapUpCount(data);
            setWrapUp(true);
        });

        socket.on("completed", (data) => {
            setCompleted(true);
            setWrapUpCount(0);
        });

        return () => {
            socket.off("agent_message");
            socket.off("updated_session_status");
        };
    }, [socket, API_BASE_URL]);

    // Auto play from queue
    useEffect(() => {
        if (!isPlaying && audioQueue.length > 0) {
            const nextItem = audioQueue[0];
            if (nextItem) {
                playAudio(nextItem.url, false, nextItem.speaker);
            }
        }
        }, [audioQueue, isPlaying]);

        const playAudio = (url, manual = true, speaker = null) => {
            if (currentAudioRef.current) {
                currentAudioRef.current.pause();
                currentAudioRef.current.currentTime = 0;
            }

            if (manual) {
                setAudioQueue([]);
            }

            const audio = new Audio(url);
            currentAudioRef.current = audio;
            setIsPlaying(true);
            setPlayingAudio({ url, speaker });   

            audio.play().catch((err) => {
                console.error("Audio play error:", err);
                setIsPlaying(false);
                setPlayingAudio(null);
            });

            audio.onended = () => {
                setIsPlaying(false);
                setPlayingAudio(null);
                if (!manual) {
                    setAudioQueue((q) => q.slice(1));
                }
            };
        };

    const pauseAudio = () => {
        if (currentAudioRef.current) {
            currentAudioRef.current.pause();
        }
        setIsPlaying(false);
        setAudioQueue([]);  
        setPlayingAudio(null); 
    };

    const sendMessage = () => {
        if (!inputMessage.trim()) return;
        pauseAudio();

        setMessages((prev) => [...prev, { speaker: "user", message: inputMessage, selected_agent: selectedAgents }]);
        socket.emit("therapist_message", { message: inputMessage, selected_agent: selectedAgents });

        setInputMessage("");
        setIsAgentsResponding(true); 
    };

    let userMsgCount = 0;

    return (
        <div className='w-full bg-white rounded-xl shadow-sm border border-gray-200 h-[600px] flex flex-col'>
            <ChatHeader/>
            <div ref={containerRef} className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.length === 0 && (
                    <div className="text-center text-gray-500 mt-8">
                        <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                        <p className="text-lg font-medium">
                            Welcome to the Therapy Simulator
                        </p>
                        <p className="text-sm mt-2">
                            Start by greeting the couple or addressing their concerns.
                        </p>
                    </div>
                )}

                {messages.map((msg, index) => {
                    let eftSkill = null;
                    let stage = null;
                    let eftName = null;
                    let eftDescription = null;

                    if (msg.speaker === 'user') {
                        eftSkill = sessionStatus.eft?.[userMsgCount]?.skill || 'N/A';
                        eftName = sessionStatus.eft?.[userMsgCount]?.name || '';
                        eftDescription = sessionStatus.eft?.[userMsgCount]?.subskill || 'No EFT skill detected yet';
                        stage = sessionStatus.stage?.[userMsgCount] || 'N/A';
                        userMsgCount++;
                    }

                    return (
                        <div
                            key={index}
                            className={`flex flex-col gap-5 ${
                                msg.speaker === 'Alex'
                                    ? 'items-start'
                                    : msg.speaker === 'Jordan'
                                    ? 'items-end'
                                    : 'items-center'
                            }`}
                        >
                            <div
                                className={`rounded-lg px-4 py-3 max-w-xl ${
                                    msg.speaker === 'Alex'
                                        ? 'bg-teal-50 border border-teal-200 text-teal-700'
                                        : msg.speaker === 'Jordan'
                                        ? 'bg-blue-50 border border-blue-200 text-blue-700'
                                        : 'bg-purple-700 border border-purple-300 text-white'
                                }`}
                            >
                                <div className="flex gap-3">
                                    <div className='flex gap-3 justify-between w-full'>
                                        <p className="text-sm font-medium capitalize mb-1 flex items-center gap-2">
                                            {msg.speaker === 'Alex'
                                                ? 'Alex'
                                                : msg.speaker === 'Jordan'
                                                ? 'Jordan'
                                                : 'You'}
                                        </p>
                                        {msg.audio && (
                                            <div className="flex gap-2 items-center">
                                                {msg.audio && playingAudio?.url === `${API_BASE_URL}${msg.audio}` && (
                                                    <span className="flex items-center gap-1 text-fuchsia-600 text-xs">
                                                    <span className="w-2 h-2 bg-fuchsia-500 rounded-full animate-pulse"></span>
                                                    Playing
                                                    </span>
                                                )}
                                                {msg.audio && (
                                                    <button
                                                    onClick={() => {
                                                        if (playingAudio?.url === `${API_BASE_URL}${msg.audio}` && isPlaying) {
                                                        pauseAudio();
                                                        } else {
                                                        playAudio(`${API_BASE_URL}${msg.audio}`, true);
                                                        }
                                                    }}
                                                    className="p-1 rounded-full hover:bg-gray-200"
                                                    >
                                                    {msg.audio && playingAudio?.url === `${API_BASE_URL}${msg.audio}` && isPlaying ? (
                                                        <Pause className="w-4 h-4" />
                                                        ) : (
                                                        <Play className="w-4 h-4" />
                                                    )}
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                        {msg.speaker === 'user' && msg.selected_agent && (
                                            <div className="bg-gray-200 rounded-full px-2 py-1 mb-2 text-xs text-gray-800">
                                                → {msg.selected_agent}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <p
                                    className={`flex ${
                                        msg.speaker === 'user' ? 'text-white' : 'text-gray-800'
                                    }`}
                                >
                                    {msg.message}
                                </p>                                
                            </div>
                                {/* {msg.speaker === 'user' && (
                                    <div className="rounded-lg px-4 py-3 max-w-md bg-gray-50 border border-gray-200 text-xs text-center">
                                        <div className='text-purple-700 font-semibold mb-2'>
                                            Stage:{' '}
                                            <span>{sessionStatus.stage?.length > 0
                                            ? stage
                                            : "N/A"}</span> 
                                             • 
                                            EFT Skill:{' '}
                                            <span className="font-semibold">{eftSkill}</span>
                                        </div>
                                        <div
                                            className={`flex flex-col justify-center gap-1 rounded-lg px-4 py-2 bg-fuchsia-100 border border-fuchsia-200 text-fuchsia-700 ${
                                                eftName ? 'text-left' : 'text-center'
                                            }`}
                                            >
                                            {eftName && <div className="font-semibold">{eftName}</div>}
                                            <div>{eftDescription}</div>
                                        </div> 
                                    </div>
                                )} */}
                        </div>
                    );
                })}

                {completed && (
                    <div className="flex justify-center">
                        <div className="bg-fuchsia-50 border border-fuchsia-200 rounded-lg px-6 py-4 max-w-lg text-center">
                            <div className="flex items-center justify-center space-x-2 mb-2">
                                <div className="w-4 h-4 bg-fuchsia-500 rounded-full"></div>
                                <p className="text-fuchsia-800 font-semibold">Session Complete!</p>
                            </div>
                            <p className="text-fuchsia-700">This session has concluded. Thank you!</p>
                        </div>
                    </div>
                )}

                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {isAgentsResponding && (
                        <div className="text-center text-gray-600 italic mb-2 flex items-center justify-center space-x-2">
                        <svg
                            className="animate-spin h-5 w-5 text-gray-500"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            />
                            <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v8H4z"
                            />
                        </svg>
                        <span>Clients are responding...</span>
                        </div>
                    )}
                </div>
            </div>

             {/* {!completed && wrapUpCount !== null && wrapUpCount > 0 && (
                <div className="p-3 bg-yellow-50/50 border border-yellow-200">
                    <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-yellow-500 animate-pulse rounded-full"></div>
                        <p className="text-yellow-800 font-medium">Session Wrap-up</p>
                    </div>
                    <p className="text-yellow-700 text-sm mt-1">
                        {wrapUpCount} more exchanges before automatic completion.
                    </p>
                </div>
            )} */}

            <div className="border-t border-gray-200 p-4">
                {/* Agent Selection */}
                <div className="mb-3">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 block">
                        Address:
                    </label>
                    <div className="flex space-x-2">
                        <button
                            onClick={() => setSelectedAgents('both')}
                            disabled = {completed}
                            className={`px-3 py-1 text-sm rounded-lg transition-colors flex items-center space-x-1 ${
                                selectedAgents === 'both'
                                    ? 'bg-purple-600 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            <Users className="h-3 w-3" />
                            <span>Both</span>
                        </button>
                        <button
                            onClick={() => setSelectedAgents('Alex')}
                            disabled = {completed}
                            className={`px-3 py-1 text-sm rounded-lg transition-colors flex items-center space-x-1 ${
                                selectedAgents === 'Alex'
                                    ? 'bg-teal-600 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            <UserCheck className="h-3 w-3" />
                            <span>Alex</span>
                        </button>
                        <button
                            onClick={() => setSelectedAgents('Jordan')}
                            disabled = {completed}
                            className={`px-3 py-1 text-sm rounded-lg transition-colors flex items-center space-x-1 ${
                                selectedAgents === 'Jordan'
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            <UserCheck className="h-3 w-3" />
                            <span>Jordan</span>
                        </button>
                    </div>
                </div>

                {/* Input Box */}
                <div className="flex space-x-3">
                    <input
                        type="text"
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        disabled = {completed}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                sendMessage();
                            }
                        }}
                        placeholder={`Enter your therapeutic intervention ${
                            selectedAgents === 'both'
                                ? 'to both agents'
                                : selectedAgents === 'Alex'
                                ? 'to Alex'
                                : 'to Jordan'
                        }`}
                        className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    <button
                        onClick={sendMessage}
                        disabled = {completed}
                        className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                    >
                        <Send className="h-4 w-4" />
                        <span>Send</span>
                    </button>
                </div>
            </div>
        </div>
    );
}
